import { useState, Dispatch, SetStateAction, useEffect } from "react";
import { FetchBidDecisionsByGroupQuery, TenderState, FetchBidDecisionsByGroupDocument, FetchBidDecisionsByGroupQueryVariables } from "@/graphql/__generated__/graphql";
import { updateTenderState } from "@/cache/Mutations";
import { useQuery } from "@apollo/client/react";

/**
 * Custom hook to manage polling for Bid Decisions by group.
 *
 * This hook uses the `useFetchBidDecisionsByGroupQuery` to poll the BidDecisionGroups
 * of a user's org at a specified interval and updates the BidDecisionGroup as well as the associated Tender's state in cache.
 * Polling can be controlled via the `isPolling` state.
 *
 * @returns {Object} An object containing:
 * - `data`: The data returned from the query.
 * - `loading`: Boolean indicating if the query is currently loading.
 * - `isPolling`: Boolean indicating if polling is active.
 * - `startPolling`: Function to start polling.
 * - `stopPolling`: Function to stop polling.
 * - `setIsPolling`: Function to set the polling state.
 * - `useRefetch`: Function to refetch the query and update the Tender's state in cache.
 */
export const useFetchBidDecisionsByGroupPolling = (bidDecisionGroupID: string, tenderID: string): {
  data: FetchBidDecisionsByGroupQuery | undefined,
  loading: boolean,
  isPolling: boolean,
  startPolling: (_pollInterval: number) => void,
  stopPolling: () => void,
  setIsPolling: Dispatch<SetStateAction<boolean>>;
  useRefetch: (_state: TenderState) => void
} => {
  const [isPolling, setIsPolling] = useState(true);

  const { data, loading, refetch, startPolling, stopPolling } = useQuery<FetchBidDecisionsByGroupQuery, FetchBidDecisionsByGroupQueryVariables>(
    FetchBidDecisionsByGroupDocument, {
      variables: {
        bidDecisionGroupId: Number(bidDecisionGroupID)
      },
      pollInterval: 60000, // Poll every minute
      skip: !isPolling,  // Control polling with state
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: false, // Prevents Apollo from updating the loading state during polling refetches
    });

  useEffect(() => {
    if (data) {
      updateTenderState(data?.fetchBidDecisionsByGroup.tenderState, tenderID);
      setIsPolling(true);
    }
  }, [data]);

  const useRefetch = (tenderState: TenderState) => {
    refetch();
    updateTenderState(tenderState, tenderID);
  };

  return { data, loading, isPolling, startPolling, stopPolling, setIsPolling, useRefetch };
};
